import { FC, useEffect, useMemo, useState, KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import {
  ErrorMessage,
  Label,
  SplitLine,
  TextMessage,
  Title,
  StyledButton
} from "../../components/generic";
import { ContainerLogin, ContainerRedirect } from "../../components/containers";
import {
  InputLabel,
  InputLabelPassword,
  useInput
} from "../../components/Input";
import { EMPTY, REGISTER_ROUTE } from "../../utils/constants";
import {
  INCORRECT_LOGIN_ERROR_MESSAGE,
  validateEmail,
  validateLength
} from "../../utils/validators";
import { useContextHospi } from "../../context/ContextHospi";
import { theme } from "styles/theme";
import { LoaderCustom } from "components/Loader";
import { clearLocalStorage } from "utils/utilAmplify";
import { HeaderSEO } from "components/headerSEO";
import { ISeo } from "interfaces/interfaces";
import { useHospiNavigate } from "../../utils/common";
import { SocialAuth } from "components/SocialAuth";

import { StyledContainer, StyledAuthButton, TextLink } from "./styles";

const Login: FC = () => {
  const navigate = useHospiNavigate();
  const { t, i18n } = useTranslation("login");
  const { isLoadingCurrentUser, signIn, logOut, forgotPassword } =
    useContextHospi();
  // Input data
  const email = useInput(process.env.REACT_APP_AWS_USER_EMAIL as string);
  const password = useInput(process.env.REACT_APP_AWS_USER_PASS as string);
  // Switchers states
  const [loginWrong, setLoginWrong] = useState(EMPTY);
  const [createdNewPass, setCreatedNewPass] = useState<0 | 1 | 2>(0);
  const [showEmailError, setShowEmailError] = useState(false);
  const [showPassError, setShowPassError] = useState(false);
  const [loading, setLoading] = useState(false);
  const seo = t("seo", { returnObjects: true }) as ISeo;
  const [isLoading, setIsLoading] = useState(false);

  // Front-end validators
  const emailValidationStatus = validateEmail({ line: email.value });
  const passwordValidationStatus = validateLength({
    line: password.value,
    params: { min: 8, max: 20 }
  });

  useEffect(() => {
    logOut(false);

    return () => {
      setCreatedNewPass(0);
      setLoginWrong(EMPTY);
    };
  }, []);

  const location = useLocation();
  const [showUnrecognizedLoginMessage, setShowUnrecognizedLoginMessage] =
    useState(false);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const redirectSearchParam = queryParams.get("redirect");
    if (queryParams.get("unknownProfile")) {
      setShowUnrecognizedLoginMessage(true);
    }

    if (redirectSearchParam) {
      try {
        // Store the redirect URL in session storage
        sessionStorage.setItem("redirectUrl", redirectSearchParam);
      } catch (err) {
        // E.g. sessionStorage may throw QuotaExceededError if is full
        console.error("sessionStorage error", err);
      }
    }
  }, [location, navigate]);

  const errorMessage = useMemo(() => {
    if (
      (loginWrong === INCORRECT_LOGIN_ERROR_MESSAGE &&
        i18n.language === "nl") ||
      (loginWrong === "E-mailadres en wachtwoord komen niet overeen." &&
        i18n.language === "gb")
    ) {
      return t("error.userNotAuthorized");
    }
    return loginWrong;
  }, [loginWrong, i18n.language]);

  const handleLogin = async () => {
    if (loading || isLoadingCurrentUser) {
      return;
    }
    setLoading(true);
    clearLocalStorage();
    await signIn(email.value as string, password.value as string)
      .then((cognitoUser) => {
        window.dataLayer.push({
          event: "login",
          user_id: cognitoUser.id,
          user_type: cognitoUser.role
        });
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        const errMessage =
          password.value === "" || password.value === undefined
            ? INCORRECT_LOGIN_ERROR_MESSAGE
            : (err.message as string);
        setLoginWrong(errMessage);
      });
  };

  const handleEnter = async (e: KeyboardEvent<HTMLInputElement>) => {
    if (
      e.key === "Enter" &&
      ![
        emailValidationStatus.message === EMPTY,
        !emailValidationStatus.state,
        passwordValidationStatus.message === EMPTY,
        !passwordValidationStatus.state
      ].some((item) => item)
    ) {
      await handleLogin();
    }
  };

  // When changed password show info message
  if (createdNewPass === 2) {
    return (
      <StyledContainer>
        <ContainerRedirect>
          <Title m="0 0 24px 0">{t("message.titleSuccessChangePass")}</Title>
          <Label lineHeight="150%" p="0">
            {t("login.textReset2")}
          </Label>
        </ContainerRedirect>
      </StyledContainer>
    );
  }

  // When changed password logics
  if (createdNewPass === 1) {
    const handleResetPass = () => {
      setIsLoading(true);
      forgotPassword(email.value as string)
        .then(() => {
          setCreatedNewPass(2);
          setLoginWrong(EMPTY);
        })
        .catch((err) => {
          setLoginWrong(err.message);
        })
        .finally(() => {
          setIsLoading(false);
        });
    };

    return (
      <ContainerLogin>
        <Title m="0 0 24px 0">{t(`login.titleReset`)}</Title>
        <TextMessage style={{ wordBreak: "break-word" }}>
          {t("login.textReset")}
        </TextMessage>
        <InputLabel
          {...email}
          title={t("login.email")}
          type="email"
          placeholder={t("login.placeholderemail")}
          p="24px 0 0 0"
          onBlur={() => {
            setShowEmailError(true);
          }}
          onFocus={() => {
            setShowEmailError(false);
          }}
          error={!emailValidationStatus.state && showEmailError}
        >
          <ErrorMessage error={!emailValidationStatus.state && showEmailError}>
            {t("error.wrongEmail")}
          </ErrorMessage>
        </InputLabel>
        <Label
          p="0"
          fontColor={loginWrong === EMPTY ? theme.colors.bgSecondary : "red"}
          fontWeight="400"
        >
          {errorMessage}
        </Label>
        {isLoading ? (
          <LoaderCustom size={5} />
        ) : (
          <StyledAuthButton
            main
            fontWeight="400"
            onClick={handleResetPass}
            w="100%"
            disabled={!emailValidationStatus.state || !email.value}
            m="24px 0"
          >
            {t(`login.buttonSendPassToEmail`)}
          </StyledAuthButton>
        )}
        <SplitLine>
          <hr />
          <label>{t("login.splitline")}</label>
        </SplitLine>
        <StyledButton
          fontWeight="400"
          onClick={() => setCreatedNewPass(0)}
          w="100%"
          m="24px 0 0 0"
        >
          {t(`login.buttonFirst`)}
        </StyledButton>
      </ContainerLogin>
    );
  }

  return (
    <ContainerLogin>
      {seo && <HeaderSEO {...seo} />}
      {(loading || isLoadingCurrentUser) && <LoaderCustom size={5} />}
      <Title m="0 0 24px 0">{t(`login.title`)}</Title>
      <form style={{ width: "100%" }}>
        <InputLabel
          {...email}
          title={t("login.email")}
          type="email"
          placeholder={t("login.placeholderemail")}
          onBlur={() => {
            setShowEmailError(true);
          }}
          onFocus={() => {
            setShowEmailError(false);
          }}
          error={!emailValidationStatus.state && showEmailError}
          onKeyPress={handleEnter}
        >
          <ErrorMessage error={!emailValidationStatus.state && showEmailError}>
            {t("error.wrongEmail")}
          </ErrorMessage>
        </InputLabel>
        <InputLabelPassword
          {...password}
          title={t("login.password")}
          type="password"
          placeholder="********"
          onBlur={() => {
            setShowPassError(true);
          }}
          onFocus={() => {
            setShowPassError(false);
          }}
          error={
            (!passwordValidationStatus.state ||
              password.value === "" ||
              password.value === undefined) &&
            showPassError
          }
          onKeyPress={handleEnter}
        >
          <ErrorMessage
            error={!passwordValidationStatus.state && showPassError}
          >
            {t("error.wrongPassword")}
          </ErrorMessage>
        </InputLabelPassword>
        <Label
          p="0"
          fontColor={loginWrong === EMPTY ? theme.colors.bgSecondary : "red"}
          fontWeight="400"
        >
          {errorMessage}
        </Label>
        <TextLink
          onClick={() => setCreatedNewPass(1)}
          role="button"
          aria-label="Reset password"
        >
          {t("login.forgotPass")}
        </TextLink>
      </form>
      <StyledButton
        fontWeight="400"
        onClick={handleLogin}
        w="100%"
        m="24px 0 24px 0"
      >
        {t(`login.buttonFirst`)}
      </StyledButton>

      <SocialAuth />
      {showUnrecognizedLoginMessage && (
        <TextMessage style={{ marginTop: "20px", color: "red" }}>
          {t("login.googleUnrecognized")}
        </TextMessage>
      )}
      <SplitLine>
        <hr />
        <label>{t("login.splitline")}</label>
      </SplitLine>
      <StyledButton
        outline
        fontWeight="400"
        onClick={(): void => {
          navigate(REGISTER_ROUTE);
        }}
        w="100%"
        m="24px 0 0 0"
      >
        {t(`login.buttonSecond`)}
      </StyledButton>
    </ContainerLogin>
  );
};

export default Login;
